import './PartnerIntro.scss';

import { marked } from 'marked';

import TextImage from '@components/TextImage/TextImage';
import { BRIDGE_BIO_CAMPAIGN_CODE } from '@constants/bridgeBio';

interface PartnerIntroProps {
  employerName: string;
  campaignCode?: string;
}

export const PartnerIntro = ({ employerName, campaignCode }: PartnerIntroProps) => {
  const isBridgeBio = campaignCode === BRIDGE_BIO_CAMPAIGN_CODE;
  const partnerName = <span dangerouslySetInnerHTML={{ __html: marked.parseInline(employerName) || '' }} />;

  return (
    <section className='partner-intro'>
      <TextImage
        desktopImageSrc='/img/partner-launch/working.webp '
        mobileImageSrc='/img/partner-launch/working.webp'
        desktopBackgroundImageSrc='/img/block-backgrounds/dark-blue-white-dots-background.webp'
        mobileBackgroundImageSrc='/img/block-backgrounds/dark-blue-white-dots-mobile.webp'
        mobileLayout='text-image'
        desktopLayout='text-image'
        imagePosition='bottom'
        alt='People discussing healthcare'
      >
        {isBridgeBio ? (
          <>
            <h2 className='partner-intro__title'>Could ADH1 run in your family?</h2>
            <p>
              {partnerName} has partnered with MyOme to offer no-charge family cascade genetic testing and counseling to
              biological relatives (up to 4th degree) of patients with ADH1, autosomal dominant hypocalcemia type 1, a
              genetic cause of low blood calcium. Family cascade genetic testing is a way for relatives to discover who
              may have inherited the same genetic change as another family member.
            </p>
            <p>
              ADH1 is caused by a genetic change (variant) in the <i>CASR</i> gene that produces a calcium sensing
              receptor.<sup>1, 2</sup> This at-home test checks whether you carry the same exact variant - so you can
              find out early, and identify potential symptoms or complications.
            </p>
            <p>
              Knowing your status lets you and your care team plan monitoring and management proactively, and helps the
              rest of your family understand their own risk.
            </p>
            <p>
              <strong>ADH1 can run in families. Knowing your status is the first step.</strong>
            </p>
          </>
        ) : (
          <>
            <h2 className='partner-intro__title'>Understand your personal health risks.</h2>
            <p>
              {partnerName} has partnered with MyOme to offer clinical-grade genomic screening and genetic counseling.
            </p>
            <p>
              You now have access to advanced genomic insights that can help you better understand your risk for common
              and rare conditions like heart disease, diabetes, and cancer.
            </p>
            <p>
              By learning about your genetic risk early, you can take proactive steps—such as lifestyle changes,
              preventative screenings, and other interventions—that may lower your chances of developing disease or
              catch it at an earlier, more treatable stage.
            </p>
            <p>
              <strong>It&apos;s your genome. Make the most of it.</strong>
            </p>
          </>
        )}
      </TextImage>
    </section>
  );
};
